/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import {
  Button, ProgressBar,
} from 'react-bootstrap';
import { FiDownload } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Document } from 'types/Invitation';
import handlePromise from 'utils/Promise';
import InvitationsServices from 'services/api/Invitations';
import { LoadingContext } from 'context/Loading';
import { ModalErrorContext } from 'context/ModalError';
import {
  createBlobUrlFromBase64DataUrl,
  downloadFileFromBase64,
  isPdfDocument,
  isValidBase64Payload,
} from 'utils/downloadFileFromBase64';
import PdfViewerModal from 'components/Modal/PdfViewerModal';
import useDownloadAll from 'hooks/useDownloadAll';
import LinkInvitationFooter from '../Footer';
import { buildDocumentFolderTreeFromLevel, DocumentFolderTree } from '../RenderDocumentFolderTree';

type LinkInvitationStep5Props = {
  documents: Document[],
  uuid: string
  onCloseInvitationAccess: () => void;
};

function LinkInvitationStep5( {
  documents, uuid,
  onCloseInvitationAccess,
}: LinkInvitationStep5Props ) {
  const { toggleLoading } = useContext( LoadingContext );
  const { openErrorModal } = useContext( ModalErrorContext );

  const [showPdfModal, setShowPdfModal] = useState( false );
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null>( null );
  const [currentDocumentoUid, setCurrentDocumentoUid] = useState<string | null>( null );
  const [pdfUrls, setPdfUrls] = useState<Record<string, string>>( {} );
  const pdfUrlsRef = useRef<Record<string, string>>( {} );

  useEffect( () => {
    pdfUrlsRef.current = pdfUrls;
  }, [pdfUrls] );

  useEffect( () => () => {
    Object.values( pdfUrlsRef.current ).forEach( ( url ) => URL.revokeObjectURL( url ) );
  }, [] );

  const clearPdfCache = useCallback( ( documentoUid: string ) => {
    setPdfUrls( ( prev ) => {
      const next = { ...prev };
      delete next[documentoUid];
      return next;
    } );
  }, [] );

  const openPdfModal = useCallback( () => setShowPdfModal( true ), [] );

  const closePdfModal = useCallback( () => {
    setShowPdfModal( false );
    setCurrentPdfUrl( null );
    setCurrentDocumentoUid( null );
  }, [] );

  const handleViewDocument = useCallback(
    async (
      documentoUid: string,
      tokenDescarga: string,
      nombre: string,
      forceRefresh = false,
    ) => {
      const isPdf = isPdfDocument( null, nombre );
      const cached = !forceRefresh && pdfUrls[documentoUid];

      if ( isPdf && cached ) {
        setCurrentPdfUrl( cached );
        setCurrentDocumentoUid( documentoUid );
        openPdfModal();
        return;
      }

      if ( !isPdf ) {
        clearPdfCache( documentoUid );
      }

      toggleLoading( true );

      const [error, response, downloadDocumentData] = await handlePromise(
        InvitationsServices.getInvitationDocuemntDownload( {
          InvitacionUid: uuid || '',
          DocumentoUid: documentoUid,
          TokenDescarga: tokenDescarga,
        } ),
      );

      toggleLoading( false );

      if ( error || !response?.ok || !downloadDocumentData ) {
        const detail = ( response as any )?.data?.detail;
        openErrorModal( detail || 'No se pudo obtener el documento. Intente de nuevo.' );
        return;
      }

      if ( !downloadDocumentData.includes( 'base64,' ) ) {
        openErrorModal( 'No se pudo obtener el documento. Intente de nuevo.' );
        return;
      }

      if ( !isValidBase64Payload( downloadDocumentData ) ) {
        if ( !isPdf ) {
          openErrorModal( 'El documento está dañado o vacío. Intente de nuevo.' );
        }
        return;
      }

      if ( isPdf ) {
        try {
          const blobUrl = createBlobUrlFromBase64DataUrl( downloadDocumentData, nombre );
          setPdfUrls( ( prev ) => ( { ...prev, [documentoUid]: blobUrl } ) );
          setCurrentPdfUrl( blobUrl );
          setCurrentDocumentoUid( documentoUid );
          openPdfModal();
        } catch {
          openErrorModal( 'El documento está dañado o vacío. Intente de nuevo.' );
        }
        return;
      }

      try {
        downloadFileFromBase64( downloadDocumentData, nombre );
      } catch {
        openErrorModal( 'No se pudo descargar el documento. Intente de nuevo.' );
      }
    },
    [uuid, pdfUrls, openPdfModal, clearPdfCache, openErrorModal],
  );

  const handlePdfLoadError = useCallback( () => {
    if ( !currentDocumentoUid ) return;
    const doc = documents.find( ( d ) => d.documentoUid === currentDocumentoUid );
    if ( !doc?.tokenDescarga ) return;
    clearPdfCache( currentDocumentoUid );
    setCurrentPdfUrl( null );
    closePdfModal();
    handleViewDocument(
      currentDocumentoUid,
      doc.tokenDescarga,
      doc.nombre,
      true,
    );
  }, [
    currentDocumentoUid,
    documents,
    clearPdfCache,
    closePdfModal,
    handleViewDocument,
  ] );

  const tree = buildDocumentFolderTreeFromLevel( documents, 7 );

  const { t } = useTranslation();

  const handleDownloadAllError = useCallback( ( message: string ) => {
    openErrorModal( message );
  }, [openErrorModal] );

  const {
    estado: downloadAllEstado,
    progreso: downloadAllProgreso,
    total: downloadAllTotal,
    failedDocuments: downloadAllFailed,
    startDownload: startDownloadAll,
  } = useDownloadAll( uuid, handleDownloadAllError );

  useEffect( () => {
    if ( downloadAllFailed.length > 0 ) {
      const fileList = downloadAllFailed.join( '\n' );
      openErrorModal(
        `${t( 'admin.invitations.downloadAll.partialMessage' )}\n\n${fileList}`,
        'warning',
        t( 'admin.invitations.downloadAll.partialTitle' ),
      );
    }
  }, [downloadAllFailed, openErrorModal, t] );

  const downloadAllPercentage = downloadAllTotal > 0
    ? Math.round( ( downloadAllProgreso / downloadAllTotal ) * 100 )
    : 0;

  return (
    <>
      <h1 className="text--blue2 text-center">Documentación</h1>
      <p className="text--gray20 mt-4 pt-2">
        Consulte o descargue la documentación disponible
      </p>
      <DocumentFolderTree
        nodes={tree}
        pdfUrls={pdfUrls}
        handleViewDocument={handleViewDocument}
        setCurrentPdfUrl={setCurrentPdfUrl}
        setShowPdfModal={setShowPdfModal}
      />

      <div className="mt-5 pt-3 text-end">
        {downloadAllEstado === 'idle' && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="fw-normal"
            onClick={startDownloadAll}
            disabled={documents.length === 0}
          >
            <FiDownload size={14} className="me-1" />
            {t( 'admin.invitations.downloadAll.button' )}
          </Button>
        )}

        {downloadAllEstado === 'processing' && (
          <div className="mt-1">
            <span className="text--gray20 text--sm">
              {t( 'admin.invitations.downloadAll.preparing' )}
              {' '}
              {downloadAllProgreso}
              /
              {downloadAllTotal}
              {' '}
              (
              {downloadAllPercentage}
              %)
            </span>
            <ProgressBar
              now={downloadAllPercentage}
              animated
              striped
              className="mt-1"
              style={{ height: '10px' }}
            />
          </div>
        )}
      </div>

      <div className="mt-5 pt-5">
        <LinkInvitationFooter
          onNext={onCloseInvitationAccess}
          nextText="Salir"
          showNextIcon={false}
        />
      </div>

      {showPdfModal && currentPdfUrl && (
        <PdfViewerModal
          url={currentPdfUrl}
          onClose={closePdfModal}
          onLoadError={handlePdfLoadError}
        />
      )}
    </>
  );
}

export default LinkInvitationStep5;
